import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

// Module-level helpers for inline-editable q/a so React keeps stable
// component identity (focus survives keystrokes during edit).
const QText = ({ block, onEdit, it, i, className }) => (
  <E block={block} onEdit={onEdit} field={`items.${i}.question`} value={it.question || it.title || ''} tag="p" className={className} placeholder="Question" />
);
const AText = ({ block, onEdit, it, i, className }) => (
  <E block={block} onEdit={onEdit} field={`items.${i}.answer`} value={it.answer || it.description || ''} tag="p" className={className} placeholder="Answer" />
);

/* ════════════════════════════════════════════════════════════════════════
 * NEW BLOCK TYPE — faq
 * Variants: accordion · two-column · numbered-steps
 *
 * Iter 175 — Section Audit. title · eyebrow · tagline AND each item's
 * question + answer are inline-editable when `E.onEdit` is wired.
 * ════════════════════════════════════════════════════════════════════════ */
export function FaqBlock({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const layout = block.layout || 'accordion';
  const items = c.items || [
    { question: 'How do I get started?', answer: 'Sign up free and explore — no credit card required.' },
    { question: 'What does it cost?', answer: 'Plans start at $0/month. Premium tiers unlock more features.' },
    { question: 'Can I cancel anytime?', answer: 'Yes. Cancel from your settings — no questions asked.' },
    { question: 'Do you offer support?', answer: 'Yes — email + live chat on all paid plans, community on free.' },
  ];

  if (layout === 'numbered-steps') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto">
          <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'How It Works'} tag="p" className="text-xs uppercase tracking-[0.22em] opacity-70 mb-3" placeholder="Eyebrow" />
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Easy steps to get started'} tag="h2" className="text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight" style={{ fontFamily: theme.headingFont || 'Syne, sans-serif' }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || ''} tag="p" className="mt-3 opacity-70 max-w-xl" placeholder="Tagline" />
          <ol className="mt-10 space-y-8">
            {items.map((it, i) => (
              <li key={i} className="flex gap-5">
                <span className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ background: theme.accent || '#7C3AED', fontFamily: theme.headingFont }}>{String(i+1).padStart(2,'0')}</span>
                <div>
                  <QText block={block} onEdit={onEdit} it={it} i={i} className="font-bold text-lg" />
                  <AText block={block} onEdit={onEdit} it={it} i={i} className="mt-1 text-sm opacity-75 leading-relaxed" />
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    );
  }

  if (layout === 'two-column') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'Your questions answered'} tag="h2" className="text-3xl md:text-4xl font-bold text-center mb-12" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {items.map((it, i) => (
              <div key={i}>
                <QText block={block} onEdit={onEdit} it={it} i={i} className="font-bold text-sm" />
                <AText block={block} onEdit={onEdit} it={it} i={i} className="mt-2 text-sm opacity-75 leading-relaxed" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // ── searchable-tabbed · Iter 189c Phase B ──────────────────────────
  //  Search bar hero + category tab chips + accordion list. Great for
  //  large help-center-style FAQs.
  if (layout === 'searchable-tabbed') {
    const categories = c.categories || ['All', 'Getting started', 'Billing', 'Account'];
    return (
      <section data-block-type={block.type} data-layout={block.layout || 'default'} className="px-6 py-24" style={{ background: theme.bg || '#FAF6EE', color: theme.ink || '#0E0E23' }}>
        <div className="max-w-3xl mx-auto text-center">
          <E block={block} onEdit={onEdit} field="eyebrow" value={c.eyebrow || 'HELP CENTER'} tag="p" className="text-xs uppercase tracking-[0.24em] opacity-60" placeholder="Eyebrow" />
          <E block={block} onEdit={onEdit} field="title" value={c.title || 'How can we help?'} tag="h2" className="mt-3 text-4xl md:text-5xl font-bold" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
          <E block={block} onEdit={onEdit} field="tagline" value={c.tagline || 'Search our knowledge base or browse by topic.'} tag="p" className="mt-3 opacity-70" placeholder="Tagline" />

          {/* Search bar */}
          <div className="mt-8 flex items-center gap-3 mx-auto max-w-xl px-5 py-3.5 rounded-full" style={{ background: '#fff', border: `1px solid ${theme.ink || '#0E0E23'}15`, boxShadow: '0 10px 30px rgba(15,15,35,0.06)' }}>
            <span aria-hidden style={{ opacity: 0.5 }}>🔍</span>
            <span className="text-sm opacity-50 text-left flex-1">Search &ldquo;how do I&hellip;&rdquo;</span>
            <span className="text-xs uppercase tracking-wider font-bold px-2 py-1 rounded" style={{ background: `${theme.ink || '#0E0E23'}08`, opacity: 0.6 }}>⌘K</span>
          </div>

          {/* Category chips */}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            {categories.map((cat, i) => (
              <span key={i} className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" style={{ background: i === 0 ? (theme.accent || '#8B5CF6') : `${theme.ink || '#0E0E23'}08`, color: i === 0 ? '#fff' : theme.ink }}>{cat}</span>
            ))}
          </div>
        </div>

        {/* Accordion */}
        <div className="mt-12 max-w-3xl mx-auto space-y-3">
          {items.map((it, i) => (
            <details key={i} className="group px-6 py-4" style={{ background: '#fff', borderRadius: theme.radius || 12, border: `1px solid ${theme.ink || '#0E0E23'}10`, boxShadow: '0 2px 8px rgba(15,15,35,0.04)' }}>
              <summary className="flex justify-between items-center cursor-pointer font-bold text-sm list-none">
                <QText block={block} onEdit={onEdit} it={it} i={i} className="flex-1" />
                <span className="text-xl opacity-50 group-open:rotate-45 transition-transform ml-3">+</span>
              </summary>
              <AText block={block} onEdit={onEdit} it={it} i={i} className="mt-3 text-sm opacity-75 leading-relaxed" />
            </details>
          ))}
        </div>
      </section>
    );
  }

  // accordion (default)
  return (
    <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
      <div className="max-w-3xl mx-auto">
        <E block={block} onEdit={onEdit} field="title" value={c.title || 'Frequently asked questions'} tag="h2" className="text-3xl md:text-4xl font-bold text-center mb-10" style={{ fontFamily: theme.headingFont }} placeholder="Headline" />
        <div className="space-y-3">
          {items.map((it, i) => (
            <details key={i} className="group border border-current/10 rounded-xl px-5 py-4" style={{ borderRadius: theme.radius || 12 }}>
              <summary className="flex justify-between items-center cursor-pointer font-bold text-sm list-none">
                <QText block={block} onEdit={onEdit} it={it} i={i} className="flex-1" />
                <span className="text-xl opacity-50 group-open:rotate-45 transition-transform ml-3">+</span>
              </summary>
              <AText block={block} onEdit={onEdit} it={it} i={i} className="mt-3 text-sm opacity-75 leading-relaxed" />
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}
